// ============================================================
// TMR Portal Admin — the decisions behind creating a Portal Interno login (33)
// ============================================================
import { ApiError } from '@tmr/api-client';
import type { LegacyProfile, LegacyProfileScreen, UserListItem } from './contract';

/** The ScriptCase compares `expira_em` with its own clock, which runs on Brasília time. */
const BRASILIA = 'America/Sao_Paulo';

/**
 * Today in Brasília as `yyyy-mm-dd` — the date the server measures "future" against (33 §3, step 4.2).
 *
 * @param now The current instant; injectable so tests do not depend on the machine's clock or time zone.
 */
export function brasiliaToday(now: Date = new Date()): string {
  // en-CA formats as yyyy-mm-dd, which is exactly the ISO date the API takes.
  return new Intl.DateTimeFormat('en-CA', { timeZone: BRASILIA, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(now);
}

/** The first date the expiry field accepts: tomorrow in Brasília. */
export function minimumExpiry(now: Date = new Date()): string {
  const [year, month, day] = brasiliaToday(now).split('-').map(Number);
  const tomorrow = new Date(Date.UTC(year, month - 1, day + 1));
  return tomorrow.toISOString().slice(0, 10);
}

/** Whether an ISO date is after today in Brasília. Empty or malformed is not. */
export function isFutureExpiry(value: string, now: Date = new Date()): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && value > brasiliaToday(now);
}

/**
 * Whether a profile can be granted. A profile whose template opens nothing would make a login that signs in
 * to an empty menu — two profiles with active people are like that in Development (33 §5).
 */
export function isGrantableProfile(profile: LegacyProfile): boolean {
  return profile.grantedScreenCount > 0;
}

/** The option label: the name, then who uses it and what it opens. */
export function profileOptionLabel(profile: LegacyProfile): string {
  const people = profile.activeStaffCount === 1 ? '1 pessoa' : `${profile.activeStaffCount} pessoas`;
  if (!isGrantableProfile(profile)) return `${profile.name} · ${people} · não libera nenhuma tela`;
  const screens = profile.grantedScreenCount === 1 ? '1 tela' : `${profile.grantedScreenCount} telas`;
  return `${profile.name} · ${people} · ${screens}`;
}

const FLAG_LABELS: ReadonlyArray<[keyof LegacyProfileScreen, string]> = [
  ['insert', 'incluir'],
  ['update', 'alterar'],
  ['delete', 'excluir'],
  ['export', 'exportar'],
  ['print', 'imprimir'],
];

/**
 * What a screen lets the login do beyond opening it, in the operator's words — "só ver" when it is nothing
 * else. Opening is implied: the preview lists only screens the template opens.
 */
export function screenAbilities(screen: LegacyProfileScreen): string {
  const granted = FLAG_LABELS.filter(([flag]) => screen[flag] === true).map(([, label]) => label);
  return granted.length === 0 ? 'só ver' : `ver, ${granted.join(', ')}`;
}

/** Filters the preview by a free-text term over the label and the app name. */
export function filterScreens(screens: LegacyProfileScreen[], term: string): LegacyProfileScreen[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return screens;
  return screens.filter((s) => s.label.toLowerCase().includes(needle) || s.screen.toLowerCase().includes(needle));
}

/**
 * Whether the screen offers "Criar acesso no Portal Interno" for an account.
 *
 * Only for an active account with no legacy login yet — a linked one already has access, and creating a
 * second login is exactly what the link picker exists to avoid. The permission and the environment
 * capability are both required: without the legacy connection the routes do not exist at all.
 */
export function canOfferLegacyAccess(
  user: Pick<UserListItem, 'legacyUserId' | 'disabled'>,
  hasPermission: boolean,
  environmentHasIt: boolean,
): boolean {
  return hasPermission && environmentHasIt && user.legacyUserId === null && !user.disabled;
}

/** What a failed creation tells the operator, and the legacy login to link instead when there is one. */
export interface GrantFailure {
  message: string;
  /** Set when a single staff login already answers for the e-mail: the screen offers "Vincular" to it. */
  linkableLegacyUserId: number | null;
}

/** Fallback for anything that is not a readable API failure. */
const GENERIC_FAILURE = 'Não foi possível criar o acesso agora. Nada foi gravado. Tente novamente.';

/**
 * Turns the server's refusal into the line the modal shows (33 §9).
 *
 * The detail the server writes is already in PT-BR and specific (it names the profile, the existing id),
 * so it is shown as is; the only thing read out of the body is the `legacyUserId` of a 409
 * `legacy.login_exists`, which is what makes the next step one click.
 */
export function describeGrantFailure(error: unknown): GrantFailure {
  if (!(error instanceof ApiError)) return { message: GENERIC_FAILURE, linkableLegacyUserId: null };

  const existing = error.extensions.legacyUserId;
  const linkable = error.code === 'legacy.login_exists' && typeof existing === 'number' ? existing : null;

  // A 404 without a known code here means the routes are not mapped: an environment without the legacy.
  if (error.status === 404 && error.code === null)
    return { message: 'A criação de acesso ao Portal Interno não está disponível neste ambiente.', linkableLegacyUserId: null };

  return { message: error.detail || GENERIC_FAILURE, linkableLegacyUserId: linkable };
}
